import React, { useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  PhoneIcon,
  EnvelopeIcon,
  ShareIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  CursorArrowRaysIcon,
} from '@heroicons/react/24/outline';

interface Action {
  id: string;
  label: string;
  type: 'url' | 'deep_link' | 'phone' | 'email' | 'share' | 'quiz_answer' | 'custom';
  value: string;
  icon?: string;
  style?: 'primary' | 'secondary' | 'success' | 'danger' | 'outline';
  position?: number;
}

interface ActionDesignerProps {
  onChange?: (actions: any[]) => void;
}

const actionTypes = [
  { value: 'url', label: 'Open URL', icon: LinkIcon },
  { value: 'deep_link', label: 'App Deep Link', icon: ArrowTopRightOnSquareIcon },
  { value: 'phone', label: 'Call Phone', icon: PhoneIcon },
  { value: 'email', label: 'Send Email', icon: EnvelopeIcon },
  { value: 'share', label: 'Share Content', icon: ShareIcon },
  { value: 'quiz_answer', label: 'Quiz Answer', icon: QuestionMarkCircleIcon },
  { value: 'custom', label: 'Custom Action', icon: CursorArrowRaysIcon },
];

const buttonStyles = [
  { value: 'primary', label: 'Primary', class: 'bg-blue-600 text-white' },
  { value: 'secondary', label: 'Secondary', class: 'bg-gray-600 text-white' },
  { value: 'success', label: 'Success', class: 'bg-green-600 text-white' },
  { value: 'danger', label: 'Danger', class: 'bg-red-600 text-white' },
  { value: 'outline', label: 'Outline', class: 'border-2 border-gray-400 text-gray-700' },
];

export default function ActionDesigner({ onChange }: ActionDesignerProps) {
  const [actions, setActions] = useState<Action[]>([]);

  const addAction = () => {
    if (actions.length >= 3) {
      alert('Maximum 3 action buttons allowed');
      return;
    }
    
    const newAction: Action = {
      id: Date.now().toString(),
      label: 'Action Button',
      type: 'url',
      value: '',
      style: 'primary',
      position: actions.length,
    };
    
    const updated = [...actions, newAction];
    setActions(updated);
    onChange?.(updated);
  };

  const updateAction = (id: string, updates: Partial<Action>) => {
    const updated = actions.map(a => 
      a.id === id ? { ...a, ...updates } : a
    );
    setActions(updated);
    onChange?.(updated);
  };

  const deleteAction = (id: string) => {
    const filtered = actions.filter(a => a.id !== id);
    // Update positions
    const updated = filtered.map((a, index) => ({ ...a, position: index }));
    setActions(updated);
    onChange?.(updated);
  };

  const moveAction = (id: string, direction: 'up' | 'down') => {
    const index = actions.findIndex(a => a.id === id);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === actions.length - 1)) {
      return;
    }
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newActions = [...actions];
    [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
    
    const updated = newActions.map((a, i) => ({ ...a, position: i }));
    setActions(updated);
    onChange?.(updated);
  };

  const getPlaceholder = (type: string) => {
    switch (type) {
      case 'url': return 'https://example.com';
      case 'deep_link': return 'app://screen/path';
      case 'phone': return '+1234567890';
      case 'email': return 'user@example.com';
      case 'share': return 'Check out this content!';
      case 'quiz_answer': return 'Answer A';
      default: return 'Action value...';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Action Buttons
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Add interactive buttons to your notification (max 3)
          </p>
        </div>
        <button
          type="button"
          onClick={addAction}
          disabled={actions.length >= 3}
          className={`w-full sm:w-auto flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
            actions.length >= 3
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Action
        </button>
      </div>

      {actions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
          <CursorArrowRaysIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No action buttons added. Click "Add Action" to create interactive buttons.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {actions.map((action, index) => {
            const ActionIcon = actionTypes.find(t => t.value === action.type)?.icon || LinkIcon;
            const styleClass = buttonStyles.find(s => s.value === action.style)?.class || '';
            
            return (
              <div
                key={action.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Button {index + 1}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => moveAction(action.id, 'up')}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveAction(action.id, 'down')}
                      disabled={index === actions.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteAction(action.id)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Button Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button Label
                      </label>
                      <input
                        value={action.label}
                        onChange={(e) => updateAction(action.id, { label: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        placeholder="Button text..."
                        maxLength={30}
                      />
                    </div>

                    {/* Button Style */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Style
                      </label>
                      <select
                        value={action.style}
                        onChange={(e) => updateAction(action.id, { style: e.target.value as Action['style'] })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {buttonStyles.map(style => (
                          <option key={style.value} value={style.value}>
                            {style.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Action Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Action Type
                    </label>
                    <select
                      value={action.type}
                      onChange={(e) => updateAction(action.id, { type: e.target.value as Action['type'] })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      {actionTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {action.type === 'url' && 'URL to open'}
                      {action.type === 'deep_link' && 'Deep link path'}
                      {action.type === 'phone' && 'Phone number'}
                      {action.type === 'email' && 'Email address'}
                      {action.type === 'share' && 'Share text'}
                      {action.type === 'quiz_answer' && 'Answer value'}
                      {action.type === 'custom' && 'Custom value'}
                    </label>
                    <input
                      value={action.value}
                      onChange={(e) => updateAction(action.id, { value: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      placeholder={getPlaceholder(action.type)}
                    />
                  </div>

                  {/* Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Preview
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 sm:p-4 flex items-center justify-center">
                      <button className={`px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium text-sm sm:text-base flex items-center ${styleClass}`}>
                        <ActionIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                        {action.label || 'Button Label'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Buttons Preview */}
      {actions.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-3">
            How buttons will appear in notification:
          </p>
          <div className="flex flex-col space-y-2">
            {actions.map(action => {
              const ActionIcon = actionTypes.find(t => t.value === action.type)?.icon || LinkIcon;
              const styleClass = buttonStyles.find(s => s.value === action.style)?.class || '';
              
              return (
                <button
                  key={action.id}
                  className={`w-full px-3 py-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base flex items-center justify-center ${styleClass}`}
                >
                  <ActionIcon className="w-4 h-4 mr-1 sm:mr-2" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}